"use client";

import { useState } from "react";
import Link from "next/link";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { formatDate } from "@/lib/format-datetime";
import { queryKeys } from "@/lib/query-keys";
import { useDebouncedValue } from "@/features/playground/lib/use-debounced-value";
import { changeMyUsername, checkUsernameAvailability, fetchMyUsername, type MyUsername } from "../api";

export const MY_USERNAME_KEY = ["me", "username"] as const;

/** The shape rule the API applies (`username-rules.ts`), checked here before asking. */
const USERNAME_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_-]{1,18}[A-Za-z0-9]$/;

type Hint = { tone: "ok" | "bad" | "muted"; text: string } | null;

/**
 * `EX-IDN02` (ADR-0088 decision 6): the person's username in Profile and
 * settings. The name made from their email can be kept (one click confirms
 * it) or changed for free once; after that, once every 90 days. Capitals are
 * the person's own and never count as a change. The check runs as they type,
 * after a pause.
 */
export function UsernameCard({ locale }: { locale: string }) {
  const t = useTranslations("dashboard.profile.username");
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: MY_USERNAME_KEY, queryFn: fetchMyUsername, staleTime: 30_000 });
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const value = draft ?? data?.usernameDisplay ?? "";
  const trimmed = value.trim();
  const debounced = useDebouncedValue(trimmed, 400);
  const sameKey = data !== undefined && trimmed.toLowerCase() === data.username;
  const shapeOk = USERNAME_SHAPE.test(trimmed);
  const windowClosed = data?.usernameNextChangeAt ? Date.parse(data.usernameNextChangeAt) > Date.now() : false;

  const availability = useQuery({
    queryKey: [...MY_USERNAME_KEY, "availability", debounced],
    queryFn: () => checkUsernameAvailability(debounced),
    enabled: data !== undefined && !sameKey && shapeOk && debounced === trimmed,
    staleTime: 30_000,
  });

  const save = useMutation({
    mutationFn: (username: string) => changeMyUsername(username),
    onSuccess: (next: MyUsername) => {
      queryClient.setQueryData(MY_USERNAME_KEY, next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
      void queryClient.invalidateQueries({ queryKey: ["me", "activity-profile"] });
      setDraft(null);
      setSaved(true);
    },
  });

  if (!data) return null;

  const nextDate = data.usernameNextChangeAt ? formatDate(data.usernameNextChangeAt, locale) : null;

  let hint: Hint = null;
  if (save.error instanceof ApiError) {
    if (save.error.code === "USERNAME_CHANGE_TOO_SOON") {
      const at = (save.error.details as { nextChangeAt?: string } | undefined)?.nextChangeAt;
      hint = { tone: "bad", text: t("window", { date: formatDate(at ?? null, locale) }) };
    } else if (save.error.code === "USERNAME_TAKEN") {
      hint = { tone: "bad", text: t("taken") };
    } else if (save.error.status === 400) {
      const reason = (save.error.details as { reason?: string } | undefined)?.reason;
      hint = { tone: "bad", text: reason === "reserved" ? t("reserved") : t("invalid") };
    } else {
      hint = { tone: "bad", text: t("error") };
    }
  } else if (save.error) {
    hint = { tone: "bad", text: t("error") };
  } else if (sameKey) {
    hint = saved && draft === null ? { tone: "ok", text: t("saved") } : { tone: "muted", text: t("current") };
  } else if (trimmed.length > 0 && !shapeOk) {
    hint = { tone: "bad", text: t("invalid") };
  } else if (windowClosed && nextDate) {
    hint = { tone: "bad", text: t("window", { date: nextDate }) };
  } else if (availability.data && availability.data.username.toLowerCase() === trimmed.toLowerCase()) {
    const a = availability.data;
    hint = a.available
      ? { tone: "ok", text: t("available") }
      : { tone: "bad", text: a.reason === "reserved" ? t("reserved") : a.reason === "invalid" ? t("invalid") : t("taken") };
  } else if (trimmed.length > 0 && shapeOk) {
    hint = { tone: "muted", text: t("checking") };
  }

  // Confirming or re-capitalising the current name is always allowed; a new
  // name needs the check's "available" and an open window.
  const recased = sameKey && trimmed !== data.usernameDisplay;
  const confirmOnly = sameKey && !recased && !data.usernameChosen;
  const canSaveNew = !sameKey && shapeOk && !windowClosed && availability.data?.available === true && availability.data.username.toLowerCase() === trimmed.toLowerCase();
  const canSave = (recased || confirmOnly || canSaveNew) && !save.isPending;
  const address = `/${locale}/u/${encodeURIComponent(data.username)}`;

  return (
    <div className="flex flex-col gap-4">
      {!data.usernameChosen ? <p className="max-w-prose text-sm text-neutral-600">{t("freeChange")}</p> : null}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="profile-username" className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          {t("label")}
        </label>
        <Input
          id="profile-username"
          value={value}
          maxLength={20}
          autoComplete="username"
          spellCheck={false}
          aria-describedby="profile-username-hint profile-username-rules"
          onChange={(e) => {
            setDraft(e.target.value);
            setSaved(false);
            save.reset();
          }}
        />
        <p
          id="profile-username-hint"
          aria-live="polite"
          className={
            hint?.tone === "ok" ? "text-xs text-emerald-700" : hint?.tone === "bad" ? "text-xs text-red-600" : "text-xs text-neutral-500"
          }
        >
          {hint?.text ?? " "}
        </p>
        <p id="profile-username-rules" className="text-xs text-neutral-500">
          {t("rules")}
        </p>
      </div>
      <p className="text-xs text-neutral-500">
        {t("profileAddress")}{" "}
        <Link className="text-primary-ink hover:underline" href={address}>
          {`progsity.io${address}`}
        </Link>
      </p>
      <p className="max-w-prose text-xs text-neutral-500">
        {data.usernameChosen && nextDate ? t("window", { date: nextDate }) : t("windowRule")}
      </p>
      <div className="flex justify-end">
        <Button disabled={!canSave} onClick={() => save.mutate(trimmed)}>
          {confirmOnly ? t("confirm") : t("save")}
        </Button>
      </div>
    </div>
  );
}
